import React, { useState, useEffect } from 'react';
import { ArrowDownLeft, AlertCircle, Plus, Camera, Paperclip, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/common/Combobox';
import { arrivalService, ArrivalItemInput } from '@/services/arrivalService';
import { cycleService } from '@/services/cycleService';
import { ArrivalWeightsGrid } from '@/components/cycles/ArrivalWeightsGrid';
import { SupplierRecord, ProductionCycleRecord } from '@/types/database';

interface PigArrivalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (arrival: any) => void;
  cycleId?: string | null;
  cycleCode?: string;
}

export const PigArrivalModal: React.FC<PigArrivalModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  cycleCode,
}) => {
  const [selectedCycleId, setSelectedCycleId] = useState<string>(cycleId || '');
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);

  const [arrivalDate, setArrivalDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [supplierId, setSupplierId] = useState<string>('');
  const [headCount, setHeadCount] = useState<number | ''>(100);
  const [totalWeightKg, setTotalWeightKg] = useState<number | ''>('');
  const [basePricePerHead, setBasePricePerHead] = useState<number | ''>('');
  const [excessWeightRate, setExcessWeightRate] = useState<number | ''>('');
  const [excessWeightAmount, setExcessWeightAmount] = useState<number | ''>('');
  const [truckingCost, setTruckingCost] = useState<number | ''>('');
  const [deliveryReceiptNo, setDeliveryReceiptNo] = useState<string>('');
  const [vehiclePlateNo, setVehiclePlateNo] = useState<string>('');
  const [driverName, setDriverName] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Granular Scale Items Grid
  const [scaleItems, setScaleItems] = useState<ArrivalItemInput[]>([]);
  const [showScaleGrid, setShowScaleGrid] = useState<boolean>(false);

  // File / Photo Attachment
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (cycleId) {
      setSelectedCycleId(cycleId);
    }
  }, [cycleId]);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);

    arrivalService.getSuppliers().then((sups) => {
      setSuppliers(sups);
      if (sups.length > 0 && !supplierId) {
        setSupplierId(sups[0].id);
      }
    });

    if (!cycleId) {
      cycleService.getCycles({ status: 'active' }).then((c) => {
        setCycles(c);
        if (c.length > 0 && !selectedCycleId) {
          setSelectedCycleId(c[0].id);
        }
      });
    }
  }, [isOpen, cycleId]);

  // When scale items change, if user hasn't explicitly overridden total weight, auto-sum
  const handleScaleItemsChange = (items: ArrivalItemInput[]) => {
    setScaleItems(items);
    if (items.length > 0) {
      const sum = items.reduce((s, it) => s + (Number(it.weight_kg) || 0), 0);
      setTotalWeightKg(Number(sum.toFixed(2)));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const targetCycleId = cycleId || selectedCycleId;
    if (!targetCycleId) {
      setErrorMsg('Please select a Production Cycle for this arrival.');
      return;
    }
    if (!arrivalDate) {
      setErrorMsg('Arrival Date is required.');
      return;
    }
    if (!headCount || Number(headCount) <= 0) {
      setErrorMsg('Head Count must be greater than zero.');
      return;
    }

    let finalWeight: number | null = null;
    if (totalWeightKg !== '' && totalWeightKg !== null && totalWeightKg !== undefined) {
      const numWeight = Number(totalWeightKg);
      if (isNaN(numWeight) || numWeight <= 0) {
        setErrorMsg('Total Intake Weight (kg) must be greater than zero if entered.');
        return;
      }
      finalWeight = numWeight;
    } else if (scaleItems.length > 0) {
      const itemsSum = scaleItems.reduce((s, it) => s + (Number(it.weight_kg) || 0), 0);
      if (itemsSum > 0) {
        finalWeight = itemsSum;
      }
    }

    setLoading(true);
    try {
      const baseAmt = (Number(basePricePerHead) || 0) * Number(headCount);
      const excessAmt = Number(excessWeightAmount) || 0;
      const trucking = Number(truckingCost) || 0;
      const totalCost = baseAmt + excessAmt + trucking;

      const created = await arrivalService.createArrival(
        {
          cycle_id: targetCycleId,
          supplier_id: supplierId || null,
          arrival_date: arrivalDate,
          head_count: Number(headCount),
          total_weight_kg: finalWeight,
          base_price_per_head: Number(basePricePerHead) || 0,
          base_amount: baseAmt,
          excess_weight_rate: Number(excessWeightRate) || 0,
          excess_weight_amount: excessAmt,
          trucking_cost: trucking,
          total_cost: totalCost,
          delivery_receipt_no: deliveryReceiptNo.trim() || null,
          vehicle_plate_no: vehiclePlateNo.trim() || null,
          driver_name: driverName.trim() || null,
          notes: notes.trim() || null,
        },
        scaleItems.length > 0 ? scaleItems : undefined,
        attachedFile
      );

      onSuccess(created);
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to log pig arrival.');
    } finally {
      setLoading(false);
    }
  };

  const cycleOptions = cycles.map((c) => ({
    value: c.id,
    label: `${c.cycle_code} — ${c.growers?.full_name || 'Company Owned'} (${c.farms?.farm_name || 'Farm'})`,
  }));

  const supplierOptions = suppliers.map((s) => ({
    value: s.id,
    label: s.supplier_name,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">
              <ArrowDownLeft className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Log Pig Arrival / Intake
              </DialogTitle>
              <p className="text-xs text-slate-500">
                Record incoming piglet placement, weighing slip, and individual scale sheet items.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Cycle Target Banner or Select */}
          {cycleId ? (
            <div className="p-3 bg-emerald-50/60 rounded-lg border border-emerald-200 text-xs text-slate-800">
              Target Cycle: <strong className="font-mono text-emerald-900">{cycleCode || cycleId}</strong>
            </div>
          ) : (
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Target Production Cycle *</label>
              <Combobox
                options={cycleOptions}
                value={selectedCycleId}
                onChange={setSelectedCycleId}
                placeholder="Select Target Production Cycle..."
                searchPlaceholder="Search active cycles..."
              />
            </div>
          )}

          {/* Arrival Date & Supplier */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Arrival Date *</label>
              <Input
                type="date"
                value={arrivalDate}
                onChange={(e) => setArrivalDate(e.target.value)}
                className="h-10 text-xs font-mono"
                required
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Supplier / Vendor</label>
              <Combobox
                options={supplierOptions}
                value={supplierId}
                onChange={setSupplierId}
                allowAll={true}
                allLabel="None / Internal Transfer"
                placeholder="Select Piglet Supplier..."
                searchPlaceholder="Search supplier..."
              />
            </div>
          </div>

          {/* Head Count & Total Weight */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 p-3.5 rounded-lg border border-slate-200">
            <div>
              <label className="text-xs font-bold text-slate-800 block mb-1">Total Head Count *</label>
              <Input
                type="number"
                min="1"
                step="1"
                tabular
                inputMode="numeric"
                value={headCount}
                onChange={(e) => setHeadCount(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                placeholder="100"
                className="h-10 text-xs font-mono font-bold"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-800 block">
                  Total Intake Weight (kg) <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                {scaleItems.length > 0 && (
                  <span className="text-[10px] text-emerald-700 font-mono font-medium">Auto-summed from scale items</span>
                )}
              </div>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                tabular
                inputMode="decimal"
                value={totalWeightKg}
                onChange={(e) => setTotalWeightKg(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="e.g. 1850.00 (optional)"
                className="h-10 text-xs font-mono font-bold"
              />
            </div>
          </div>

          {/* Granular Individual Scale Weights Section Toggle */}
          <div className="border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-800">Paper Sheet Individual Pig Weights</span>
                <p className="text-[11px] text-slate-500">
                  Optional: Log individual scale weights directly from physical paper weighing sheets.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowScaleGrid(!showScaleGrid)}
                className="min-h-[44px] text-xs px-3"
              >
                {showScaleGrid ? 'Hide Scale Grid' : `Open Scale Grid (${scaleItems.length})`}
              </Button>
            </div>

            {showScaleGrid && (
              <div className="pt-2">
                <ArrivalWeightsGrid items={scaleItems} onChange={handleScaleItemsChange} />
              </div>
            )}
          </div>

          {/* Pricing & Delivery Details (Optional / Contract Rates) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Base Price (₱/head)</label>
              <Input
                type="number"
                step="0.01"
                tabular
                inputMode="decimal"
                value={basePricePerHead}
                onChange={(e) => setBasePricePerHead(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="₱2,800.00"
                className="h-10 text-xs font-mono"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Excess Weight Rate (₱/kg)</label>
              <Input
                type="number"
                step="0.01"
                tabular
                inputMode="decimal"
                value={excessWeightRate}
                onChange={(e) => setExcessWeightRate(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="₱120.00"
                className="h-10 text-xs font-mono"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Trucking Cost (₱)</label>
              <Input
                type="number"
                step="0.01"
                tabular
                inputMode="decimal"
                value={truckingCost}
                onChange={(e) => setTruckingCost(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder="₱3,500.00"
                className="h-10 text-xs font-mono"
              />
            </div>
          </div>

          {/* Logistics References */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Delivery Receipt #</label>
              <Input
                type="text"
                value={deliveryReceiptNo}
                onChange={(e) => setDeliveryReceiptNo(e.target.value)}
                placeholder="DR-882194"
                className="h-10 text-xs font-mono"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Vehicle Plate #</label>
              <Input
                type="text"
                value={vehiclePlateNo}
                onChange={(e) => setVehiclePlateNo(e.target.value)}
                placeholder="NBC 1234"
                className="h-10 text-xs"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Driver Name</label>
              <Input
                type="text"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                placeholder="Driver Name"
                className="h-10 text-xs"
              />
            </div>
          </div>

          {/* Document / Paper Sheet Photo Upload */}
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
            <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5 text-emerald-700" /> Paper Weighing Slip Photo / Attachment
            </label>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 transition-colors shadow-xs min-h-[44px]">
                <Paperclip className="w-3.5 h-3.5 text-slate-500" />
                {attachedFile ? attachedFile.name : 'Choose file or photo'}
                <input
                  type="file"
                  accept="image/*,.pdf"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              {attachedFile && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
                  <Check className="w-3.5 h-3.5" /> Ready to upload
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Notes</label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Batch quality, health notes, chute arrival details..."
              className="h-10 text-xs"
            />
          </div>

          <DialogFooter className="sticky bottom-0 bg-white border-t border-slate-200 pt-3 flex flex-row items-center justify-end gap-2 z-10">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading} className="min-h-[44px]">
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-600 font-semibold gap-1 min-h-[44px]"
              disabled={loading}
            >
              <Plus className="w-4 h-4" /> {loading ? 'Saving Arrival...' : 'Save Pig Arrival'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
